import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Typography,
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Collapse,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import DataStatusAlert from "../../../../AssetManagement/Health/DataAccuracy/UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";
import { IconButton } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Box } from "@mui/system";
import DcProcessLogsMoreInfo from "./DcProcessLogsMoreInfo";
import DcRunIdModal from "./DcProcessLogsRunIdModal";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  setRowsPerPage,
  setPage,
  setSort,
  setSelectedRow,
  setLogsFilterDataList,
  setTotalProcessLogs,
  setArgoPODLogData,
  setSelectedWorkFlow,
  setSelectedPodName,
} from "../../../../../features/monitoringProcessLogsSlice";
import { formatDate } from "../../../../../utils/helpers";
import DownloadIcon from "@mui/icons-material/Download";
import { saveAs } from "file-saver";
import { useMonitoringProcessLogs } from "../../../../../customHooks/useMonitoringProcessLogs";
import { ProcessLogsEndPointLogic } from "../../../../../logic/DataCollection/Monitoring/ProcessLogsEndPointLogic";
import { selectUserToken } from "../../../../../features/userSlice";

interface Props {
  tableHeadings: { [key: string]: string };
  backendSortLoading: boolean;
  setBackendSortLoading: (value: boolean) => void;
}

// headers for the table columns with sortable option for each column
const tableheaders = [
  { id: 1, KEY: "run_id", label: "Run ID", sortable: true, backendSort: true },
  { id: 2, KEY: "workflow_name", label: "Workflow Name", sortable: false },
  { id: 3, KEY: "package_name", label: "Package", sortable: false },
  { id: 4, KEY: "process_name", label: "Process Name", sortable: false },
  { id: 5, KEY: "pod_name", label: "Pod Name", sortable: false },
  {
    id: 6,
    KEY: "start_time",
    label: "First Run",
    sortable: true,
    backendSort: true,
  },
  {
    id: 7,
    KEY: "end_time",
    label: "Last Run",
    sortable: true,
    backendSort: true,
  },
  { id: 8, KEY: "status", label: "Status", sortable: false },
  { id: 9, KEY: "brand", label: "Brand", sortable: false },
  { id: 10, KEY: "interface", label: "Interface", sortable: false },
  { id: 11, KEY: "run_method", label: "Run Method", sortable: false },
  { id: 12, KEY: "step", label: "Step", sortable: false },
  { id: 13, KEY: "more_info", label: "More Info", sortable: false },
];

const DcProcessLogTable = ({
  tableHeadings,
  backendSortLoading,
  setBackendSortLoading,
}: Props) => {
  const dispatch = useDispatch();
  const { fetchMonitoringProcessLogs } = useMonitoringProcessLogs();
  const { selectedProcessLogsDateRange } = useSelector(
    (state: any) => state.monitoringProcessLogs
  );

  const {
    processLogsList,
    selectedWorkFlow,
    selectedLogsPackage,
    selectedLogsProcessName,
    selectedLogsStatus,
    selectedLogsBrand,
    selectedLogsInterface,
    selectedRunMethodList,
    selectedStepList,
    sort,
    page,
    rowsPerPage,
    isTableLoading,
    exportLoading,
    selectedPodName,
  } = useSelector((state: any) => state.monitoringProcessLogs);
  const [sortedData, setSortedData] = useState<any[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [openRowIndex, setOpenRowIndex] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [isAppending, setIsAppending] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const location = useLocation();
  const headCells = Object.entries(tableHeadings);

  const getCurrentFilters = () => ({
    package_name: selectedLogsPackage?.length ? selectedLogsPackage : undefined,
    process_id: selectedLogsProcessName?.length
      ? selectedLogsProcessName
      : undefined, // Changed from process_name
    status: selectedLogsStatus?.length ? selectedLogsStatus : undefined,
    brand_id: selectedLogsBrand?.length ? selectedLogsBrand : undefined,
    interface_id: selectedLogsInterface?.length
      ? selectedLogsInterface
      : undefined, // Added _id suffix for consistency
    run_method: selectedRunMethodList?.length
      ? selectedRunMethodList
      : undefined,
    step: selectedStepList?.length ? selectedStepList : undefined,
    workflow_name: selectedWorkFlow?.length ? selectedWorkFlow : undefined,
    pod_name: selectedPodName?.length ? selectedPodName : undefined,
  });

  //-- Sorting Data
  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        // Default sorting by created (To make new items appear first in your table when they are Created or Added)
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined) {
          return 0;
        }

        let aValue, bValue;

        aValue = a[keytoSort];
        bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    // eslint-disable-next-line
    [sort]
  );

  // Map frontend field keys to backend sort field names
  const getSortFieldMapping = (key: string) => {
    const mapping: { [key: string]: string } = {
      run_id: "run_id",
      start_time: "first_run",
      end_time: "last_run",
    };
    return mapping[key] || key;
  };

  // Check if field requires backend sorting
  const requiresBackendSort = (key: string) => {
    return ["run_id", "start_time", "end_time"].includes(key);
  };

  // -- Function to handle pagination
  const handleChangePage = async (_: unknown, newPage: number) => {
    dispatch(setPage(newPage));
    const updatedParams = new URLSearchParams(window.location.search);
    updatedParams.set("page", newPage.toString());
    setSearchParams(updatedParams);

    // Only fetch more data when reaching exactly the 100th page
    const isAtHundredthPage = newPage + 1 === 100;
    if (
      hasMoreData &&
      isAtHundredthPage &&
      !isAppending &&
      processLogsList.length > 0
    ) {
      const lastRow = processLogsList[processLogsList.length - 1];
      const runId = lastRow?.run_id;

      if (runId) {
        setIsAppending(true);

        const from_date = selectedProcessLogsDateRange[0].format("YYYY-MM-DD");
        const to_date = selectedProcessLogsDateRange[1].format("YYYY-MM-DD");
        const currentFilters = getCurrentFilters();

        try {
          const newCount = await fetchMonitoringProcessLogs(
            from_date,
            to_date,
            runId,
            processLogsList,
            currentFilters
          );
          setTimeout(() => {
            setIsAppending(false);
          }, 100);
          if (newCount < 1000) {
            setHasMoreData(false);
          }
        } catch (error) {
          console.error("Error fetching more logs:", error);
          setIsAppending(false);
        }
      }
    }
  };

  //-- Function to handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(0));
  };

  //-- Function to handle click on table headers for sorting
  const handleHeaderClick = async (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable

    const newDirection =
      header.KEY === sort.keytoSort && sort.direction === "asc"
        ? "desc"
        : "asc";

    // Check if this field requires backend sorting
    if (requiresBackendSort(header.KEY)) {
      setBackendSortLoading(true);
      try {
        const from_date = selectedProcessLogsDateRange[0].format("YYYY-MM-DD");
        const to_date = selectedProcessLogsDateRange[1].format("YYYY-MM-DD");
        const currentFilters = getCurrentFilters();
        const sortField = getSortFieldMapping(header.KEY);
        const sortOrder = newDirection.toUpperCase() as "ASC" | "DESC";

        // Make backend API call for sorted data
        const response = await ProcessLogsEndPointLogic.getSortedData(
          oauthTokenHeader,
          from_date,
          to_date,
          sortField,
          sortOrder,
          undefined, // run_id not needed for sorting
          currentFilters
        );

        if (response?.status === 200 && response?.data?.data) {
          // Update the process logs with sorted data
          dispatch(setLogsFilterDataList(response.data.data));
          // Reset pagination to first page when sorting
          dispatch(setPage(0));
          const updatedParams = new URLSearchParams(window.location.search);
          updatedParams.set("page", "0");
          setSearchParams(updatedParams);
        }

        // Update sort state
        dispatch(
          setSort({
            keytoSort: header.KEY,
            direction: newDirection,
          })
        );
      } catch (error) {
        console.error("Error fetching sorted data:", error);
      } finally {
        setBackendSortLoading(false);
      }
    } else {
      // Use existing frontend sorting for non-backend sortable fields
      dispatch(
        setSort({
          keytoSort: header.KEY,
          direction: newDirection,
        })
      );
    }
  };

  const handleToggle = (index: number) => {
    setOpenRowIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const handleClickOpen = (row: any) => {
    setOpen(true);
    dispatch(setSelectedRow(row));
  };

  const handleClose = () => {
    setOpen(false);
    dispatch(setSelectedRow({})); // Reset selected row
    dispatch(setArgoPODLogData([])); // Reset argoPODLogData
  };

  const { processLogsFilters } = useSelector(
    (state: any) => state.monitoringProcessLogs
  );
  const brandsDetails = processLogsFilters?.BRAND;

  // Create a mapping for brands lookup
  const brandsLookup = React.useMemo(() => {
    const lookup: { [key: number]: string } = {};
    if (brandsDetails?.length > 0) {
      brandsDetails.forEach((brand: any) => {
        lookup[brand.id] = brand.label;
      });
    }
    return lookup;
  }, [brandsDetails]);

  //-- Set Sorted Data
  useEffect(() => {
    const modifiedProcessLogsList =
      processLogsList?.length > 0 &&
      processLogsList?.map((item: any) => {
        const brandId = Number(item.brand_id); // Convert string to number
        return {
          ...item,
          brand: `${brandsLookup[brandId] || "Unknown"} (${item.brand_id})`,
          interface: `${item.interface_name ?? "Unknown"} ${
            item.interface_id ? `(${item.interface_id})` : ""
          }`,
        };
      });
    const sortedRows = getSortedData(modifiedProcessLogsList || []);
    setSortedData(sortedRows);
    //eslint-disable-next-line
  }, [processLogsList, getSortedData, brandsLookup]);

  //-- Set Default Page
  useEffect(() => {
    // Don't reset page if we're appending data or if we're on a valid page
    const maxValidPage = Math.max(
      0,
      Math.ceil(sortedData?.length / rowsPerPage) - 1
    );
    if (!isAppending && page > maxValidPage && sortedData?.length > 0) {
      dispatch(setPage(maxValidPage));
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("page", maxValidPage.toString());
      setSearchParams(updatedParams);
    }
    // eslint-disable-next-line
  }, [
    sortedData?.length,
    rowsPerPage,
    selectedWorkFlow,
    selectedLogsPackage,
    selectedLogsProcessName,
    selectedLogsStatus,
    selectedLogsBrand,
    selectedLogsInterface,
    selectedRunMethodList,
    selectedStepList,
    isAppending,
    selectedPodName,
  ]);

  // Add effect to reset hasMoreData when filters change
  useEffect(() => {
    setHasMoreData(true);
  }, [
    selectedWorkFlow,
    selectedLogsPackage,
    selectedLogsProcessName,
    selectedLogsStatus,
    selectedLogsBrand,
    selectedLogsInterface,
    selectedRunMethodList,
    selectedStepList,
    selectedProcessLogsDateRange,
    selectedPodName,
  ]);

  useEffect(() => {
    dispatch(setPage(Number(searchParams.get("page")) || 0));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page")); // Convert to number
    dispatch(setPage(isNaN(pageFromUrl) ? 0 : pageFromUrl)); // Ensure it defaults correctly
    // eslint-disable-next-line
  }, [searchParams]);

  //-- Set Total Process Logs and filtered data list
  useEffect(() => {
    if (sortedData?.length > 0) {
      dispatch(setTotalProcessLogs(sortedData?.length));
      dispatch(setLogsFilterDataList(sortedData));
    } else {
      dispatch(setTotalProcessLogs(0));
      dispatch(setLogsFilterDataList([]));
    }
    // eslint-disable-next-line
  }, [sortedData?.length]);

  const handlePodName = (name: string) => {
    const isSameName =
      selectedPodName?.length === 1 && selectedPodName[0] === name;

    const updatedName = isSameName ? [] : [name];

    dispatch(setSelectedPodName(updatedName));

    const updatedParams = new URLSearchParams(window.location.search);

    if (updatedName.length > 0) {
      updatedParams.set("pod_name", updatedName[0]);
    } else {
      updatedParams.delete("pod_name");
    }

    setSearchParams(updatedParams);

    const [startDate, endDate] = selectedProcessLogsDateRange;

    const currentFilters = {
      ...getCurrentFilters(),
      pod_name: updatedName[0] || "",
    };

    fetchMonitoringProcessLogs(
      startDate.format("YYYY-MM-DD"),
      endDate.format("YYYY-MM-DD"),
      undefined,
      undefined,
      currentFilters
    );
  };

  // -- Function to export CSV
  const handleExportCSV = async () => {
    try {
      // 1. Prepare parameters
      const from_date = selectedProcessLogsDateRange[0].format("YYYY-MM-DD");
      const to_date = selectedProcessLogsDateRange[1].format("YYYY-MM-DD");
      const currentFilters = getCurrentFilters();

      // 2. Make API call
      const response = await ProcessLogsEndPointLogic.exportToExcel(
        oauthTokenHeader,
        from_date,
        to_date,
        "xlsx",
        currentFilters
      );

      // 3. Validate and process response
      if (!response) {
        throw new Error("Empty response received from server");
      }

      let blob;
      if (response instanceof Blob) {
        // If the endpoint already returns a Blob
        blob = response;
      } else if (typeof response === "string") {
        // If we get CSV text
        blob = new Blob([response], { type: "text/csv;charset=utf-8;" });
      } else {
        // Handle other response types (e.g., array buffer)
        blob = new Blob([response], { type: "application/octet-stream" });
      }

      // 4. Trigger download
      const filename = `process_logs_${from_date}_to_${to_date}.xlsx`;
      saveAs(blob, filename);
    } catch (error) {
      console.error("CSV Export Error:", {
        error,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleWorkFlow = (workFlowName: string) => {
    const newSelectedWorkFlow =
      selectedWorkFlow?.length === 1 && selectedWorkFlow[0] === workFlowName
        ? []
        : [workFlowName];

    dispatch(setSelectedWorkFlow(newSelectedWorkFlow));
    const updatedParams = new URLSearchParams(window.location.search);
    if (newSelectedWorkFlow.length > 0) {
      updatedParams.set("workflow_name", newSelectedWorkFlow[0]);
    } else {
      updatedParams.delete("workflow_name");
    }
    updatedParams.set("page", "0");
    setSearchParams(updatedParams);

    const from_date = selectedProcessLogsDateRange[0].format("YYYY-MM-DD");
    const to_date = selectedProcessLogsDateRange[1].format("YYYY-MM-DD");
    const currentFilters = {
      ...getCurrentFilters(),
      workflow_name:
        newSelectedWorkFlow.length > 0 ? newSelectedWorkFlow : undefined,
    };

    fetchMonitoringProcessLogs(
      from_date,
      to_date,
      undefined,
      undefined,
      currentFilters
    );

    // Reset pagination
    dispatch(setPage(0));
  };


  return (
    <>
      <Box display="flex">
        <IconButton
          color="secondary"
          size="large"
          data-tracker="374"
          sx={{ ml: "auto" }}
          onClick={handleExportCSV}
        >
          {exportLoading ? (
            <CircularProgress color="secondary" size={20} />
          ) : (
            <DownloadIcon fontSize="medium" />
          )}
        </IconButton>
      </Box>
      <TableContainer className="BrandsTable">
        <Table aria-label="collapsible table" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableheaders.map((header: any) => (
                <TableCell key={header.id} align="left">
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable || backendSortLoading}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isTableLoading || backendSortLoading ? (
              <TableRow>
                <TableCell
                  colSpan={headCells.length}
                  align="center"
                  sx={{ padding: "40px" }}
                ></TableCell>
              </TableRow>
            ) : sortedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={headCells.length}
                  align="center"
                  sx={{ padding: "20px", color: "#888", fontSize: "18px" }}
                >
                  No Data To Display
                </TableCell>
              </TableRow>
            ) : (
              <>
                {sortedData.length > 0 &&
                  sortedData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, rowIndex) => (
                      <React.Fragment key={rowIndex}>
                        <TableRow
                          sx={{
                            "& > *": {
                              borderBottom:
                                "1px solid rgba(224, 224, 224, 1) !important",
                            },
                          }}
                        >
                          {headCells.map(([key], cellIndex) => (
                            <TableCell key={cellIndex} align="left">
                              {key === "run_id" ? (
                                <>
                                  <Link
                                    to={{
                                      pathname: location.pathname,
                                      search: location.search,
                                    }}
                                    style={{
                                      textDecoration: "underline",
                                      color: "#9C27B0",
                                    }}
                                    onClick={() => {
                                      handleClickOpen(row);
                                    }}
                                    data-tracker="351"
                                  >
                                    <Typography
                                      color="secondary"
                                      fontSize="14px"
                                    >
                                      {row[key] ? row[key] : ""}
                                    </Typography>
                                  </Link>
                                </>
                              ) : key === "workflow_name" ? (
                                row[key] ? (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => handleWorkFlow(row[key])}
                                      color="secondary"
                                      sx={{ 
                                        padding: 0.5,                                       
                                        }}
                                    >
                                      <FilterAltIcon
                                        fontSize="small"
                                                               
                                      />
                                    </IconButton>

                                    {row[key]}
                                  </Box>
                                ) : (
                                  ""
                                )
                              ) : key === "package_id" ? (
                                <>{row[key] ? row[key] : ""}</>
                              ) : key === "process_name" ? (
                                <>{row[key] ? row[key] : ""}</>
                              ) : key === "pod_name" ? (
                                row[key] ? (                          
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => handlePodName(row[key])}
                                      color="secondary"
                                      sx={{ 
                                        padding: 0.5,                                       
                                        }}
                                    >
                                      <FilterAltIcon
                                        fontSize="small"
                                                               
                                      />
                                    </IconButton>

                                    {row[key]}
                                  </Box>
                                ) : (
                                  ""
                                )
                              ) : // <>{row[key] ? row[key] : ""}</>
                              key === "start_time" ? (
                                <>{row[key] ? formatDate(row[key]) : ""}</>
                              ) : key === "end_time" ? (
                                <> {row[key] ? formatDate(row[key]) : ""}</>
                              ) : key === "brand" ? (
                                <>{row[key] ? row[key] : ""}</>
                              ) : key === "interface" ? (
                                <>{row[key] ? row[key] : ""}</>
                              ) : key === "run_method" ? (
                                <>{row[key] ? row[key] : ""}</>
                              ) : key === "step" ? (
                                <>{row[key] ? row[key] : ""}</>
                              ) : key === "more_info" ? (
                                <>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggle(rowIndex)}
                                    aria-label="expand row"
                                  >
                                    {openRowIndex === rowIndex ? (
                                      <KeyboardArrowUpIcon />
                                    ) : (
                                      <KeyboardArrowDownIcon data-tracker="351" />
                                    )}
                                  </IconButton>
                                </>
                              ) : key === "status" ? (
                                <>
                                  {row.status === "SUCCESS" && (
                                    <DataStatusAlert status="Success" />
                                  )}
                                  {row.status === "FAILURE" && (
                                    <DataStatusAlert status="Failure" />
                                  )}
                                  {row.status === "CANCELLED" && (
                                    <DataStatusAlert status="Cancelled" />
                                  )}
                                  {row.status === "RUNNING" && (
                                    <DataStatusAlert status="Running" />
                                  )}
                                  {row.status === "PREEMPTED" && (
                                    <DataStatusAlert status="Warning" />
                                  )}
                                </>
                              ) : row[key] ? (
                                row[key]
                              ) : (
                                ""
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                            colSpan={12}
                          >
                            <Collapse
                              in={openRowIndex === rowIndex}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box margin={1}>
                                <DcProcessLogsMoreInfo runId={row.run_id} />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
              </>
            )}
          </TableBody>
        </Table>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={sortedData?.length}
            rowsPerPage={rowsPerPage}
            page={Math.min(
              page,
              Math.max(0, Math.ceil(sortedData?.length / rowsPerPage) - 1)
            )}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(sortedData?.length / rowsPerPage)}
            onChange={(_, value) => handleChangePage(null, value - 1)}
            page={Math.min(
              page + 1,
              Math.ceil(sortedData?.length / rowsPerPage)
            )}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sortedData?.length}
            rowsPerPage={rowsPerPage}
            page={Math.min(
              page,
              Math.max(0, Math.ceil(sortedData?.length / rowsPerPage) - 1)
            )}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack>
      </TableContainer>
      <DcRunIdModal open={open} handleClose={handleClose} />
    </>
  );
};

export default DcProcessLogTable;
